import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type { TrainingSession, TrainingSessionDetail } from "@/lib/types"

interface TrainingFilters {
  status?: string
  training_type?: string
  q?: string
  upcoming?: string
}

export function useTrainingSessions(filters?: TrainingFilters) {
  const params = new URLSearchParams()
  if (filters?.status) params.set("status", filters.status)
  if (filters?.training_type) params.set("training_type", filters.training_type)
  if (filters?.q) params.set("search", filters.q)
  if (filters?.upcoming) params.set("upcoming", filters.upcoming)
  const qs = params.toString()

  return useQuery({
    queryKey: ["training_sessions", filters],
    queryFn: () => api.get<{ data: TrainingSession[] }>(`/api/v1/training_sessions${qs ? `?${qs}` : ""}`),
  })
}

export function useTrainingSession(id: string) {
  return useQuery({
    queryKey: ["training_sessions", id],
    queryFn: () => api.get<{ data: TrainingSessionDetail }>(`/api/v1/training_sessions/${id}`),
    enabled: !!id,
  })
}

export function useCreateTrainingSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<{ data: TrainingSession }>("/api/v1/training_sessions", { training_session: data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["training_sessions"] })
    },
  })
}

export function useUpdateTrainingSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch<{ data: TrainingSession }>(`/api/v1/training_sessions/${id}`, { training_session: data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["training_sessions"] })
    },
  })
}

export function useDeleteTrainingSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/training_sessions/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["training_sessions"] })
    },
  })
}

export function useAddParticipant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ sessionId, userId }: { sessionId: string; userId: string }) =>
      api.post(`/api/v1/training_sessions/${sessionId}/participants`, { user_id: userId }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["training_sessions", vars.sessionId] })
      qc.invalidateQueries({ queryKey: ["training_sessions"] })
    },
  })
}

export function useUpdateParticipant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ sessionId, userId, data }: { sessionId: string; userId: string; data: Record<string, unknown> }) =>
      api.patch(`/api/v1/training_sessions/${sessionId}/participants/${userId}`, data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["training_sessions", vars.sessionId] })
    },
  })
}

export function useRemoveParticipant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ sessionId, userId }: { sessionId: string; userId: string }) =>
      api.delete(`/api/v1/training_sessions/${sessionId}/participants/${userId}`),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["training_sessions", vars.sessionId] })
      qc.invalidateQueries({ queryKey: ["training_sessions"] })
    },
  })
}

export function useCompleteTraining() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ data: TrainingSessionDetail }>(`/api/v1/training_sessions/${id}/complete`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["training_sessions"] })
    },
  })
}

export function useIssueCertificates() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ data: TrainingSessionDetail }>(`/api/v1/training_sessions/${id}/issue_certificates`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["training_sessions"] })
    },
  })
}
